import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/api';
import './chat.css';

const toShortDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
};

const toInitials = (value) => {
    const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
};

const isMobileDevice = () => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        if (window.matchMedia('(max-width: 900px)').matches) return true;
        if (window.matchMedia('(pointer: coarse)').matches) return true;
    }
    if (typeof navigator === 'undefined') return false;
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
};

const ChatPage = () => {
    const [searchParams] = useSearchParams();
    const requestedConversationId = Number(searchParams.get('conversationId') || 0);
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [composer, setComposer] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const streamRef = useRef(null);
    const messagesRef = useRef(null);

    useEffect(() => {
        const updateMobile = () => setIsMobile(isMobileDevice());
        updateMobile();

        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mediaQuery = window.matchMedia('(max-width: 900px), (pointer: coarse)');

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', updateMobile);
            return () => mediaQuery.removeEventListener('change', updateMobile);
        }

        if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(updateMobile);
            return () => mediaQuery.removeListener(updateMobile);
        }

        return undefined;
    }, []);

    const activeConversation = useMemo(() => conversations.find((item) => Number(item.id) === Number(activeId)) || null, [conversations, activeId]);

    const stopStream = () => {
        if (streamRef.current) {
            streamRef.current.close();
            streamRef.current = null;
        }
    };

    const loadConversations = async () => {
        try {
            const response = await api.get('/chat/conversations');
            const list = Array.isArray(response?.data) ? response.data : [];
            setConversations(list);
            if (list.length > 0) {
                const requestedExists = requestedConversationId > 0 && list.some((item) => Number(item.id) === requestedConversationId);
                if (requestedExists) {
                    setActiveId(requestedConversationId);
                } else if (!activeId) {
                    setActiveId(list[0].id);
                }
            }
        } catch (e) {
            setMessage('Failed to load chat conversations.');
        }
    };

    const loadMessages = async (conversationId) => {
        if (!conversationId) return;
        setLoading(true);
        try {
            const response = await api.get(`/chat/conversations/${conversationId}/messages`);
            setMessages(Array.isArray(response?.data?.messages) ? response.data.messages : []);
        } catch (e) {
            setMessage('Failed to load chat messages.');
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    const startStream = (conversationId) => {
        stopStream();
        const token = JSON.parse(localStorage.getItem('user') || '{}')?.token || '';
        if (!conversationId || !token) return;

        const backendUrl = String(process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
        if (!backendUrl) return;

        const stream = new EventSource(`${backendUrl}/chat/conversations/${conversationId}/stream?authToken=${encodeURIComponent(token)}`);

        stream.addEventListener('message', (event) => {
            try {
                const payload = JSON.parse(event.data);
                setMessages((prev) => {
                    if (prev.some((item) => Number(item.id) === Number(payload.id))) return prev;
                    return [...prev, payload];
                });
            } catch (_) {
                // Ignore malformed events.
            }
        });

        stream.onerror = () => {
            stream.close();
        };

        streamRef.current = stream;
    };

    useEffect(() => {
        loadConversations();
        return () => stopStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedConversationId]);

    useEffect(() => {
        if (!activeId) return;
        loadMessages(activeId);
        startStream(activeId);
        return () => stopStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeId]);

    useEffect(() => {
        const container = messagesRef.current;
        if (!container) return;
        container.scrollTop = container.scrollHeight;
    }, [messages, activeId, loading]);

    const send = async (event) => {
        event.preventDefault();
        const trimmed = composer.trim();
        if (!activeId || !trimmed) return;

        try {
            const response = await api.post(`/chat/conversations/${activeId}/messages`, { body: trimmed });
            setComposer('');
            setMessages((prev) => {
                if (prev.some((item) => Number(item.id) === Number(response?.data?.id))) return prev;
                return [...prev, response.data];
            });
            loadConversations();
        } catch (e) {
            setMessage('Unable to send message right now.');
        }
    };

    const sendAsText = async (event) => {
        event.preventDefault();
        const trimmed = composer.trim();
        const phone = activeConversation?.guestPhone;
        if (!activeId || !trimmed || !phone) return;

        try {
            const response = await api.post(`/chat/conversations/${activeId}/messages`, { body: trimmed });
            setComposer('');
            setMessages((prev) => {
                if (prev.some((item) => Number(item.id) === Number(response?.data?.id))) return prev;
                return [...prev, response.data];
            });
            loadConversations();
            window.location.href = `sms:${phone}?body=${encodeURIComponent(trimmed)}`;
        } catch (e) {
            setMessage('Unable to send message right now.');
        }
    };

    return (
        <div className="chat-page">
            <div className="chat-sidebar">
                <div className="chat-sidebar-header">
                    <div>
                        <p className="chat-kicker">Inbox</p>
                        <h3>Conversations</h3>
                    </div>
                    <span className="chat-sidebar-count">{conversations.length}</span>
                </div>
                {message && <p className="chat-error">{message}</p>}
                <ul>
                    {conversations.map((conversation) => (
                        <li key={conversation.id}>
                            <button
                                className={Number(activeId) === Number(conversation.id) ? 'chat-conversation-active' : ''}
                                onClick={() => setActiveId(conversation.id)}
                            >
                                <span className="chat-conversation-avatar">{toInitials(conversation.guestName || conversation.guestEmail)}</span>
                                <span className="chat-conversation-copy">
                                    <strong>{conversation.guestName}</strong>
                                    <span>{conversation.guestEmail}</span>
                                </span>
                                <small>{toShortDate(conversation.lastMessageAt)}</small>
                            </button>
                        </li>
                    ))}
                    {conversations.length === 0 && <li className="chat-empty chat-empty-sidebar">No conversations yet.</li>}
                </ul>
            </div>

            <div className="chat-thread">
                {activeConversation ? (
                    <>
                        {isMobile && conversations.length > 0 && (
                            <div className="chat-mobile-switcher">
                                <label className="chat-mobile-switcher-label" htmlFor="chat-mobile-conversation-select">Conversation</label>
                                <select
                                    id="chat-mobile-conversation-select"
                                    className="chat-mobile-switcher-select"
                                    value={activeId || ''}
                                    onChange={(event) => setActiveId(Number(event.target.value) || null)}
                                >
                                    {conversations.map((conversation) => (
                                        <option key={conversation.id} value={conversation.id}>
                                            {conversation.guestName || conversation.guestEmail}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="chat-thread-header">
                            <div className="chat-thread-header-main">
                                <span className="chat-conversation-avatar chat-conversation-avatar-large">{toInitials(activeConversation.guestName || activeConversation.guestEmail)}</span>
                                <div>
                                    <p className="chat-kicker">Guest conversation</p>
                                    <h3>{activeConversation.guestName}</h3>
                                    <span>{activeConversation.guestEmail}</span>
                                </div>
                            </div>
                        </div>

                        <div className="chat-messages" ref={messagesRef}>
                            {loading && <p className="chat-empty">Loading conversation...</p>}
                            {!loading && messages.map((entry) => (
                                <article key={entry.id} className={`chat-bubble ${entry.senderRole === 'host' ? 'chat-bubble-host' : 'chat-bubble-guest'}`}>
                                    <header>
                                        <strong>{entry.senderDisplay}</strong>
                                        <small>{toShortDate(entry.createdAt)}</small>
                                    </header>
                                    <p>{entry.body}</p>
                                </article>
                            ))}
                            {!loading && messages.length === 0 && <p className="chat-empty">No messages yet. Start the conversation below.</p>}
                        </div>

                        <form className="chat-composer" onSubmit={send}>
                            <textarea value={composer} onChange={(event) => setComposer(event.target.value)} rows={3} placeholder="Write a message..." />
                            <div className="chat-composer-actions">
                                <button className="btn-primary" type="submit">Send</button>
                                {isMobile && activeConversation?.guestPhone && (
                                    <button className="btn-secondary" type="button" onClick={sendAsText}>Send as Text</button>
                                )}
                            </div>
                        </form>
                    </>
                ) : (
                    <p className="chat-empty">Choose a conversation to begin.</p>
                )}
            </div>
        </div>
    );
};

export default ChatPage;
